import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartOpenEvent, CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) {
      // Fallback: use data-product-url attribute (e.g. when rendered inside cart drawer recommendations)
      const fallbackUrl = this.dataset.productUrl;
      if (fallbackUrl) {
        const url = new URL(fallbackUrl, window.location.origin);
        const selectedVariantId = this.#getSelectedVariantId();
        if (selectedVariantId) {
          url.searchParams.set('variant', selectedVariantId);
        }
        return url.toString();
      }
      return '';
    }

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    console.log('update quick add modal');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Ensure quick-buy styling doesn't leak into the standard quick add flow.
    modalContent.classList.remove('quick-add-modal__content--quick-buy');

    const productFormComponent = productGrid.querySelector('product-form-component');
    if (productFormComponent) {
      const backInStockTrigger = productFormComponent.querySelector('a.back-in-stock-trigger');
      if (backInStockTrigger) {
        backInStockTrigger.removeAttribute('id');
        backInStockTrigger.classList.add('BIS_trigger', 'bis-collection-button');
      }
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;
      productTitle.classList.add('h4');

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productPrice.classList.add('body-m-semibold');
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      // Build and append the quick-add product switcher if alternative products exist
      const productSwitcher = this.#buildProductSwitcher(productGrid);
      if (productSwitcher) {
        productGrid.appendChild(productSwitcher);
      }

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        const backInStockTrigger = productFormComponent.querySelector('a.back-in-stock-trigger');
        if (backInStockTrigger) {
          backInStockTrigger.removeAttribute('id');
          backInStockTrigger.classList.add('BIS_trigger', 'bis-collection-button');
        }
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    if (!isMobileBreakpoint()) {
      this.#ensureDesktopProductSwitcher(modalContent);
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Ensures quick-add product switcher is rendered on desktop in the modal,
   * immediately before variant picker (same visual position as mobile).
   * @param {HTMLElement} modalContent
   */
  #ensureDesktopProductSwitcher(modalContent) {
    if (modalContent.querySelector('quick-add-product-switcher')) return;

    const productSwitcher = this.#buildProductSwitcher(modalContent);
    if (!productSwitcher) return;

    const variantPicker = modalContent.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.parentNode?.insertBefore(productSwitcher, variantPicker);
      return;
    }

    const productHeader = modalContent.querySelector('.product-header');
    if (productHeader) {
      productHeader.insertAdjacentElement('afterend', productSwitcher);
      return;
    }

    modalContent.appendChild(productSwitcher);
  }

  /**
   * Builds the quick-add product switcher element from alternative products data.
   * @param {ParentNode} [sourceRoot]
   * @returns {Element | null} The product switcher element or null if no alternatives.
   */
  #buildProductSwitcher(sourceRoot) {
    const alternativeProductsData = this.dataset.alternativeProducts;
    /** @type {Array<{id: string, url: string, title: string, available?: boolean, colorName?: string, image?: string|null, swatchImage?: string|null, swatchColor?: string|null}> | null} */
    let products = null;

    try {
      if (alternativeProductsData) {
        const parsed = JSON.parse(alternativeProductsData);
        if (Array.isArray(parsed)) products = parsed;
      }
    } catch (error) {
      console.error('QuickAddComponent: Failed to parse alternative products data', error);
    }

    if (!products || products.length < 2) {
      products = this.#getProductsFromAlternateSwitcher(sourceRoot);
    }

    if (!Array.isArray(products) || products.length < 2) return null;

    const currentProductId = this.dataset.productId;
    const currentProduct = products.find((product) => product.id === currentProductId);
    const currentProductColorName = currentProduct?.colorName || currentProduct?.title || '';

    const switcher = document.createElement('quick-add-product-switcher');
    switcher.className = 'quick-add-product-switcher';
    switcher.dataset.currentProductId = currentProductId || '';

    // Add visible label
    const label = document.createElement('div');
    label.className = 'quick-add-product-switcher__label';
    label.textContent = 'Color';
    const selectedValue = document.createElement('span');
    selectedValue.className = 'variant-option__selected-value';
    selectedValue.textContent = currentProductColorName;
    label.appendChild(selectedValue);
    switcher.appendChild(label);

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'quick-add-product-switcher__fieldset';

    const legend = document.createElement('legend');
    legend.className = 'visually-hidden';
    legend.textContent = 'Color options';
    fieldset.appendChild(legend);

    products.forEach((product, index) => {
      const isSelected = product.id === currentProductId;
      const isUnavailable = product.available === false;
      const label = document.createElement('label');
      label.className = `quick-add-product-switcher__item${isSelected ? ' quick-add-product-switcher__item--selected' : ''}${isUnavailable ? ' quick-add-product-switcher__item--unavailable' : ''}`;

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `quick-add-product-${currentProductId}`;
      input.value = product.id;
      input.dataset.productId = product.id;
      input.dataset.productUrl = product.url;
      input.dataset.productTitle = product.title;
      input.dataset.productColorName = product.colorName || product.title;
      input.setAttribute('aria-label', product.title);
      if (isSelected) input.checked = true;
      if (isUnavailable) {
        input.setAttribute('data-product-available', 'false');
      }

      const swatch = document.createElement('span');
      swatch.className = 'quick-add-product-switcher__swatch';
        
        // Follow variant-main-picker.liquid priority:
        // 1. image from metafield (swatchImage)
        // 2. hex color from metafield (swatchColor)
        // 3. featured image of the product
      if (product.swatchImage) {
        const img = document.createElement('img');
        img.className = 'quick-add-product-switcher__swatch-img';
        img.src = product.swatchImage;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 18;
        img.height = 18;
        swatch.appendChild(img);
      } else if (product.swatchColor) {
        const colorSpan = document.createElement('span');
        colorSpan.className = 'quick-add-product-switcher__swatch-color';
        colorSpan.style.backgroundColor = product.swatchColor;
        swatch.appendChild(colorSpan);
      } else if (product.image) {
        const img = document.createElement('img');
        img.className = 'quick-add-product-switcher__swatch-img';
        img.src = product.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 18;
        img.height = 18;
        swatch.appendChild(img);
      }

      if (isUnavailable) {
        const slash = document.createElement('span');
        slash.className = 'quick-add-product-switcher__swatch-slash';
        slash.setAttribute('aria-hidden', 'true');
        swatch.appendChild(slash);
      }

      const visuallyHidden = document.createElement('span');
      visuallyHidden.className = 'visually-hidden';
      visuallyHidden.textContent = product.colorName || product.title;
      swatch.appendChild(visuallyHidden);

      label.appendChild(input);
      label.appendChild(swatch);
      fieldset.appendChild(label);
    });

    switcher.appendChild(fieldset);
    return switcher;
  }

  /**
   * Builds products payload from existing alternate-product-switcher markup.
   * @param {ParentNode} [sourceRoot]
   */
  #getProductsFromAlternateSwitcher(sourceRoot) {
    const root = sourceRoot || this;
    const alternateSwitcher = root.querySelector('alternate-product-switcher');
    if (!alternateSwitcher) return null;

    const inputs = alternateSwitcher.querySelectorAll('input[type="radio"][data-product-id][data-product-url]');
    if (!inputs.length) return null;

    const products = Array.from(inputs)
      .map((input) => {
        if (!(input instanceof HTMLInputElement)) return null;

        const item = /** @type {HTMLElement | null} */ (input.closest('.alternate-product-switcher__item'));
        const swatchImg = item?.querySelector('.alternate-product-switcher__swatch-img');
        const swatchColorEl = item?.querySelector('.alternate-product-switcher__swatch-color');
        const isAvailable = item?.dataset.available === 'true' || item?.dataset.altAvailable === 'true';
        const isSoldOut = item?.classList.contains('alternate-product-switcher__item--sold-out');

        return {
          id: input.dataset.productId || '',
          url: input.dataset.productUrl || '',
          title: input.dataset.productTitle || '',
          available: isSoldOut ? false : isAvailable,
          colorName: input.dataset.productColorName || input.dataset.productTitle || '',
          image: null,
          swatchImage: swatchImg instanceof HTMLImageElement ? swatchImg.src : null,
          swatchColor: swatchColorEl instanceof HTMLElement ? swatchColorEl.style.backgroundColor || null : null,
        };
      })
      .filter((product) => product !== null && product.id && product.url && product.title);

    return /** @type {Array<{id: string, url: string, title: string, available?: boolean, colorName?: string, image?: string|null, swatchImage?: string|null, swatchColor?: string|null}>} */ (products);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const modalInputs = Array.from(modalContent.querySelectorAll('input[type="radio"][data-variant-id]')).filter(
      (input) => input instanceof HTMLInputElement
    );

    if (!modalInputs.length) return;

    const selectedVariantId = this.#getSelectedVariantId();
    const matchedInput = selectedVariantId
      ? modalInputs.find((input) => input.dataset.variantId === selectedVariantId)
      : null;

    if (matchedInput) {
      if (!matchedInput.checked) {
        matchedInput.checked = true;
        matchedInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const hasCheckedInput = modalInputs.some((input) => input.checked);
    if (hasCheckedInput) return;

    const firstAvailableInput = modalInputs.find((input) => input.dataset.optionAvailable !== 'false');
    const fallbackInput = firstAvailableInput || modalInputs[0];
    if (!fallbackInput) return;

    fallbackInput.checked = true;
    fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleBackInStockClick, { signal: this.#abortController.signal });
    this.addEventListener('click', this.#handleUnavailableOptionClick, { capture: true, signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * Closes the dialog when back-in-stock trigger is clicked
   * @param {MouseEvent} event - The click event
   */
  #handleBackInStockClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('.back-in-stock-trigger')) return;
    this.closeDialog();
  };

  #handleUnavailableOptionClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const unavailableLabel = target?.closest('.product-option-not-available');
    if (!unavailableLabel) return;

    const modalContent = unavailableLabel.closest('#quick-add-modal-content');
    const trigger = modalContent?.querySelector('.BIS_trigger');
    if (!trigger) return;

    const input = unavailableLabel.querySelector('input');
    if (input) {
      trigger.dataset.variantId = input.dataset.variantId ?? '';
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

/**
 * Global Quick Buy
 *
 * Triggers off any element matching: [data-quick-add-button][data-product-handle]
 * Loads /products/{handle}?view=quick-buy and injects into the existing quick-add modal.
 */
class GlobalQuickBuy {
  /** @type {AbortController | null} */
  static #abortController = null;
  /** @type {AbortController | null} */
  static #bindController = null;
  static #isBound = false;

  /** @type {WeakMap<Element, any[]>} */
  static #variantsCache = new WeakMap();

  static init() {
    if (this.#isBound) return;
    this.#isBound = true;
    document.addEventListener('click', this.#handleTriggerClick);
  }

  /** @param {Event} event */
  static #handleTriggerClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-quick-add-button][data-product-handle]');
    if (!trigger) return;

    const handle = trigger.getAttribute('data-product-handle');
    if (!handle) return;

    event.preventDefault();
    event.stopPropagation();

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof DialogComponent)) return;

    // Ensure trigger has proper dialog a11y attributes so DialogComponent can sync aria-expanded.
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-haspopup', trigger.getAttribute('aria-haspopup') ?? 'dialog');
      trigger.setAttribute('aria-controls', trigger.getAttribute('aria-controls') ?? 'quick-add-dialog');
      trigger.setAttribute('aria-expanded', trigger.getAttribute('aria-expanded') ?? 'false');
    }

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) return;

    modalContent.classList.add('quick-add-modal__content--quick-buy');
    modalContent.setAttribute('aria-busy', 'true');
    modalContent.innerHTML = GlobalQuickBuy.#renderLoading();

    dialogComponent.showDialog();

    try {
      const html = await GlobalQuickBuy.#fetchQuickBuyHtml(handle);
      modalContent.innerHTML = html;
      GlobalQuickBuy.#bind(modalContent);
    } catch (error) {
      console.error('GlobalQuickBuy: Failed to load quick buy view', error);
      modalContent.innerHTML = GlobalQuickBuy.#renderError();
    } finally {
      modalContent.removeAttribute('aria-busy');
    }
  };

  static #renderLoading() {
    return `<div class="quick-buy" data-quick-buy><div style="padding:16px;">Loading…</div></div>`;
  }

  static #renderError() {
    return `<div class="quick-buy" data-quick-buy><div style="padding:16px;">Sorry — something went wrong.</div></div>`;
  }

  /**
   * @param {string} handle
   * @returns {Promise<string>}
   */
  static async #fetchQuickBuyHtml(handle) {
    const normalized = handle.replace(/^\/+/, '').trim();
    if (!normalized) throw new Error('Missing product handle');

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const url = `/products/${encodeURIComponent(normalized)}?view=quick-buy`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'text/html' },
      signal: this.#abortController.signal,
    });

    if (!response.ok) throw new Error(`Failed to fetch quick-buy HTML: ${response.status}`);
    const html = await response.text();
    this.#abortController = null;
    return html;
  }

  /** @param {HTMLElement} modalContent */
  static #bind(modalContent) {
    this.#bindController?.abort();
    this.#bindController = new AbortController();
    const { signal } = this.#bindController;

    // Cache variants JSON for each panel.
    modalContent.querySelectorAll('[data-quick-buy-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const script = panel.querySelector('script[data-quick-buy-variants]');
      if (!(script instanceof HTMLScriptElement)) return;

      try {
        const parsed = JSON.parse(script.textContent || '[]');
        if (Array.isArray(parsed)) this.#variantsCache.set(panel, parsed);
      } catch (_) {
        // ignore
      }

      // Availability is based on the active panel; initial refresh happens below.
    });

    // If there is no explicit active panel, activate the first.
    const firstPanel = modalContent.querySelector('[data-quick-buy-panel]');
    const activePanel = this.#getActivePanel(modalContent);
    if (!activePanel && firstPanel instanceof HTMLElement) {
      this.#setActivePanel(modalContent, firstPanel.getAttribute('data-handle') || '');
    }

    // Ensure we have a selected size.
    this.#ensureHasSelectedSize(modalContent);

    const activePanelNow = this.#getActivePanel(modalContent);
    if (activePanelNow) {
      this.#refreshSizeAvailability(modalContent, activePanelNow);
    }

    modalContent.addEventListener(
      'click',
      (e) => {
        const el = e.target instanceof Element ? e.target : null;
        if (!el) return;

        const swatch = el.closest('[data-quick-buy-color]');
        if (swatch instanceof HTMLButtonElement) {
          const handle = swatch.getAttribute('data-handle') || '';
          this.#setActivePanel(modalContent, handle);
          return;
        }

        const sizeBtn = el.closest('[data-quick-buy-size]');
        if (sizeBtn instanceof HTMLButtonElement) {
          this.#selectSize(modalContent, sizeBtn);
          this.#updateAddButtonState(modalContent);
          return;
        }

        const addBtn = el.closest('[data-quick-buy-add]');
        if (addBtn instanceof HTMLButtonElement) {
          this.#addToCart(modalContent).catch((err) => console.error('GlobalQuickBuy: addToCart failed', err));
        }
      },
      { signal }
    );

    // Keyboard support for COLOR (tabs) and SIZE (radios)
    modalContent.addEventListener(
      'keydown',
      (e) => {
        if (!(e instanceof KeyboardEvent)) return;
        const target = e.target instanceof Element ? e.target : null;
        if (!target) return;

        const swatch = target.closest('[data-quick-buy-color]');
        if (swatch instanceof HTMLButtonElement) {
          this.#handleSwatchKeydown(modalContent, e, swatch);
          return;
        }

        const sizeBtn = target.closest('[data-quick-buy-size]');
        if (sizeBtn instanceof HTMLButtonElement) {
          this.#handleSizeKeydown(modalContent, e, sizeBtn);
        }
      },
      { signal }
    );

    this.#updateAddButtonState(modalContent);
  }

  /** @param {HTMLElement} modalContent */
  static #getActivePanel(modalContent) {
    const panel = modalContent.querySelector('.quick-buy__panel--active[data-quick-buy-panel]');
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @param {HTMLElement} modalContent
   * @param {string} handle
   */
  static #setActivePanel(modalContent, handle) {
    if (!handle) return;

    const panels = modalContent.querySelectorAll('[data-quick-buy-panel]');
    panels.forEach((p) => {
      if (!(p instanceof HTMLElement)) return;
      const isActive = (p.getAttribute('data-handle') || '') === handle;
      p.classList.toggle('quick-buy__panel--active', isActive);
      p.hidden = !isActive;
      p.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    const swatches = modalContent.querySelectorAll('[data-quick-buy-color]');
    swatches.forEach((s) => {
      if (!(s instanceof HTMLButtonElement)) return;
      const isSelected = (s.getAttribute('data-handle') || '') === handle;
      s.classList.toggle('quick-buy__swatch--selected', isSelected);
      s.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      s.tabIndex = isSelected ? 0 : -1;
    });

    const activePanel = this.#getActivePanel(modalContent);
    if (activePanel) {
      this.#ensureHasSelectedSize(modalContent);
      this.#refreshSizeAvailability(modalContent, activePanel);
    }

    this.#updateAddButtonState(modalContent);
  }

  /** @param {HTMLElement} modalContent */
  static #ensureHasSelectedSize(modalContent) {
    const selected = modalContent.querySelector('[data-quick-buy-size].is-selected');
    if (selected) return;

    const firstAvailable = modalContent.querySelector('[data-quick-buy-size][aria-disabled="false"]');
    if (firstAvailable instanceof HTMLButtonElement) {
      this.#selectSize(modalContent, firstAvailable);
      return;
    }

    const first = modalContent.querySelector('[data-quick-buy-size]');
    if (first instanceof HTMLButtonElement) this.#selectSize(modalContent, first);
  }

  /**
   * @param {HTMLElement} modalContent
   * @param {HTMLButtonElement} btn
   */
  static #selectSize(modalContent, btn) {
    /** @type {NodeListOf<HTMLButtonElement>} */
    const all = /** @type {any} */ (modalContent.querySelectorAll('[data-quick-buy-size]'));
    all.forEach((b) => {
      if (!(b instanceof HTMLButtonElement)) return;
      const isSelected = b === btn;
      b.classList.toggle('is-selected', isSelected);
      b.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      b.tabIndex = isSelected ? 0 : -1;
    });
  }

  /**
   * Update size button availability based on the active panel.
   * @param {HTMLElement} modalContent
   * @param {HTMLElement} panel
   */
  static #refreshSizeAvailability(modalContent, panel) {
    const variants = this.#variantsCache.get(panel) || [];
    const sizePos = Number(panel.getAttribute('data-size-option-position') || 0);

    if (!(sizePos >= 1 && sizePos <= 3)) return;

    /** @type {NodeListOf<HTMLButtonElement>} */
    const sizeButtons = /** @type {any} */ (modalContent.querySelectorAll('[data-quick-buy-size]'));
    sizeButtons.forEach((b) => {
      if (!(b instanceof HTMLButtonElement)) return;
      const value = b.getAttribute('data-option-value') || '';
      const match = variants.find((v) => String(v?.[`option${sizePos}`]) === String(value));
      const isAvailable = match ? match.available !== false : true;
      b.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');

      if (!isAvailable) {
        // Keep it focusable only if it is currently selected; otherwise remove from tab order.
        if (!b.classList.contains('is-selected')) b.tabIndex = -1;
      }
    });

    // After availability changes (e.g. color swap), update CTA state.
    this.#updateAddButtonState(modalContent);
  }

  /**
   * @param {HTMLElement} modalContent
   * @returns {{ id: number | null, available: boolean }}
   */
  static #getSelectedVariantInfo(modalContent) {
    const panel = this.#getActivePanel(modalContent);
    if (!panel) return { id: null, available: false };

    const variants = this.#variantsCache.get(panel) || [];
    const sizePos = Number(panel.getAttribute('data-size-option-position') || 0);
    const selectedSize = modalContent.querySelector('[data-quick-buy-size].is-selected');
    const selectedValue = selectedSize instanceof HTMLElement ? selectedSize.getAttribute('data-option-value') : null;

    if (sizePos >= 1 && sizePos <= 3 && selectedValue) {
      const anyMatch = variants.find((v) => String(v?.[`option${sizePos}`]) === String(selectedValue));
      const id = Number(anyMatch?.id);
      const available = anyMatch ? anyMatch.available !== false : false;
      return { id: Number.isFinite(id) && id > 0 ? id : null, available };
    }

    const fallback = Number(panel.getAttribute('data-initial-variant-id'));
    const id = Number.isFinite(fallback) && fallback > 0 ? fallback : null;
    return { id, available: Boolean(id) };
  }

  /**
   * @param {HTMLElement} modalContent
   * @param {KeyboardEvent} event
   * @param {HTMLButtonElement} swatch
   */
  static #handleSwatchKeydown(modalContent, event, swatch) {
    const key = event.key;
    const isActivate = key === 'Enter' || key === ' ' || key === 'Spacebar';
    const isPrev = key === 'ArrowLeft' || key === 'Left';
    const isNext = key === 'ArrowRight' || key === 'Right';
    const isHome = key === 'Home';
    const isEnd = key === 'End';
    if (!(isActivate || isPrev || isNext || isHome || isEnd)) return;

    /** @type {HTMLButtonElement[]} */
    const swatches = Array.from(modalContent.querySelectorAll('[data-quick-buy-color]')).filter(
      (el) => el instanceof HTMLButtonElement
    );

    const currentIndex = swatches.indexOf(swatch);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;
    if (isPrev) nextIndex = Math.max(0, currentIndex - 1);
    if (isNext) nextIndex = Math.min(swatches.length - 1, currentIndex + 1);
    if (isHome) nextIndex = 0;
    if (isEnd) nextIndex = swatches.length - 1;

    event.preventDefault();

    const target = swatches[nextIndex];
    if (!target) return;

    const handle = target.getAttribute('data-handle') || '';
    this.#setActivePanel(modalContent, handle);
    target.focus();
  }

  /**
   * @param {HTMLElement} modalContent
   * @param {KeyboardEvent} event
   * @param {HTMLButtonElement} sizeBtn
   */
  static #handleSizeKeydown(modalContent, event, sizeBtn) {
    const key = event.key;
    const isActivate = key === 'Enter' || key === ' ' || key === 'Spacebar';
    const isPrev = key === 'ArrowLeft' || key === 'Left';
    const isNext = key === 'ArrowRight' || key === 'Right';
    const isHome = key === 'Home';
    const isEnd = key === 'End';
    if (!(isActivate || isPrev || isNext || isHome || isEnd)) return;

    /** @type {HTMLButtonElement[]} */
    const radios = Array.from(modalContent.querySelectorAll('[data-quick-buy-size]')).filter(
      (el) => el instanceof HTMLButtonElement
    );

    const enabled = radios.filter((b) => b.getAttribute('aria-disabled') !== 'true');
    const list = enabled.length ? enabled : radios;
    const currentIndex = list.indexOf(sizeBtn);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;
    if (isPrev) nextIndex = Math.max(0, currentIndex - 1);
    if (isNext) nextIndex = Math.min(list.length - 1, currentIndex + 1);
    if (isHome) nextIndex = 0;
    if (isEnd) nextIndex = list.length - 1;

    event.preventDefault();

    const target = list[nextIndex];
    if (!target) return;

    this.#selectSize(modalContent, target);
    this.#updateAddButtonState(modalContent);
    target.focus();
  }

  /**
   * @param {HTMLElement} modalContent
   * @returns {number | null}
   */
  static #getSelectedVariantId(modalContent) {
    const info = this.#getSelectedVariantInfo(modalContent);
    return info.id;
  }

  /** @param {HTMLElement} modalContent */
  static #updateAddButtonState(modalContent) {
    const addBtn = modalContent.querySelector('[data-quick-buy-add]');
    if (!(addBtn instanceof HTMLButtonElement)) return;

    if (!addBtn.dataset.defaultLabel) {
      addBtn.dataset.defaultLabel = addBtn.textContent?.trim() || 'ADD TO BAG';
    }

    const { id: variantId, available } = this.#getSelectedVariantInfo(modalContent);

    if (!variantId) {
      addBtn.disabled = true;
      addBtn.textContent = addBtn.dataset.defaultLabel;
      return;
    }

    if (!available) {
      addBtn.disabled = true;
      addBtn.textContent = 'SOLD OUT';
      return;
    }

    addBtn.disabled = false;
    addBtn.textContent = addBtn.dataset.defaultLabel;
  }

  /** @param {HTMLElement} modalContent */
  static async #addToCart(modalContent) {
    const errorEl = modalContent.querySelector('[data-quick-buy-error]');
    const addBtn = modalContent.querySelector('[data-quick-buy-add]');
    if (!(addBtn instanceof HTMLButtonElement)) return;

    // If disabled (e.g. SOLD OUT), do nothing.
    if (addBtn.disabled) {
      const selectedSize = modalContent.querySelector('[data-quick-buy-size].is-selected');
      const isDisabledSelected =
        selectedSize instanceof HTMLElement && selectedSize.getAttribute('aria-disabled') === 'true';
      if (isDisabledSelected && errorEl instanceof HTMLElement) {
        errorEl.hidden = false;
        errorEl.textContent = 'Sold out.';
      }
      return;
    }

    const { id: variantId, available } = this.#getSelectedVariantInfo(modalContent);
    if (!variantId) {
      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = false;
        errorEl.textContent = 'Please select a size.';
      }
      return;
    }

    if (!available) {
      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = false;
        errorEl.textContent = 'Sold out.';
      }
      return;
    }

    if (errorEl instanceof HTMLElement) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    addBtn.disabled = true;
    addBtn.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      const data = await response.json();
      const didError = !response.ok;

      document.dispatchEvent(
        new CartUpdateEvent(data, 'quick-buy', {
          didError,
          source: 'quick-buy',
          variantId: String(variantId),
        })
      );

      if (!didError) {
        // Hide the quick-buy modal on success
        const dialogComponent = document.getElementById('quick-add-dialog');
        if (dialogComponent instanceof DialogComponent) {
          dialogComponent.closeDialog();
        }

        document.dispatchEvent(new CartOpenEvent());
        return;
      }

      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = false;
        errorEl.textContent = data?.description || data?.message || 'Unable to add to cart.';
      }
    } catch (error) {
      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = false;
        errorEl.textContent = 'Unable to add to cart.';
      }
    } finally {
      addBtn.removeAttribute('aria-busy');
      addBtn.disabled = false;
    }
  }
}

GlobalQuickBuy.init();

/**
 * Quick add component with variant picker that adds to cart immediately on variant selection.
 */
export class QuickAddWithVariantPickerComponent extends Component {
  #abortController = new AbortController();
  /** @type {string | null} */
  #lastSelectedVariantId = null;
  /** @type {string | null} */
  #pointerDownVariantId = null;
  /** @type {boolean} */
  #pointerDownWasChecked = false;
  /** @type {number | null} */
  #loadingTimeoutId = null;
  /** @type {boolean} Guards against double form submission from change + synthetic click events */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();
    
    // Prevent drag behavior to eliminate micro-drag delays
    this.addEventListener('dragstart', (e) => {
      e.preventDefault();
      return false;
    });
    
    // Use mousedown for instant feedback (no click delay from micro-drags)
    this.addEventListener('mousedown', this.#handleVariantMouseDown);
    this.addEventListener('touchstart', this.#handleVariantMouseDown, { passive: true });
    this.addEventListener('click', this.#handleVariantClick);
    
    this.addEventListener('change', this.#handleVariantChange);
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal: this.#abortController.signal });

    const checked = this.querySelector('input[type="radio"][data-variant-id]:checked');
    if (checked instanceof HTMLInputElement) {
      this.#lastSelectedVariantId = checked.dataset.variantId || null;
    }

    this.#ensureOverlay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mousedown', this.#handleVariantMouseDown);
    this.removeEventListener('touchstart', this.#handleVariantMouseDown);
    this.removeEventListener('click', this.#handleVariantClick);
    this.#abortController.abort();
    if (this.#loadingTimeoutId) window.clearTimeout(this.#loadingTimeoutId);
  }

  #ensureOverlay() {
    if (this.querySelector('.quick-add-variant-overlay')) return;

    const overlay = document.createElement('div');
    overlay.className = 'quick-add-variant-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = `<span class="quick-add-variant-spinner" aria-hidden="true"></span>`;
    this.appendChild(overlay);
  }

  #setLoading(isLoading) {
    const overlay = this.querySelector('.quick-add-variant-overlay');
    
    if (isLoading) {
      if (overlay) {
        // Use transform (GPU-accelerated) for instant visual feedback
        overlay.classList.add('quick-add-variant-overlay--active');
      }
      
      this.setAttribute('data-loading', '');
      
      if (this.#loadingTimeoutId) window.clearTimeout(this.#loadingTimeoutId);
      this.#loadingTimeoutId = window.setTimeout(() => {
        this.removeAttribute('data-loading');
        if (overlay) {
          overlay.classList.remove('quick-add-variant-overlay--active');
        }
        this.#loadingTimeoutId = null;
      }, 3000);
    } else {
      this.removeAttribute('data-loading');
      if (overlay) {
        overlay.classList.remove('quick-add-variant-overlay--active');
      }
      
      if (this.#loadingTimeoutId) {
        window.clearTimeout(this.#loadingTimeoutId);
        this.#loadingTimeoutId = null;
      }
    }
  }

  /**
   * Handles variant selection change and triggers add to cart
   * @param {Event} event - The change event from the variant picker input
   */
  #handleVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (!target.dataset.variantId) return;

    const isAvailable = target.dataset.optionAvailable !== 'false';
    if (!isAvailable) {
      this.#setLoading(false);
      return;
    }

    // Check if loading already set by mousedown handler
    const alreadyLoading = this.hasAttribute('data-loading');
    
    if (!alreadyLoading) {
      // Fallback for keyboard navigation (no mousedown event)
      this.#setLoading(true);
    }

    this.#lastSelectedVariantId = target.dataset.variantId;
    this.#syncFormVariantId(target.dataset.variantId);

    // Allow other change listeners (e.g. variant picker state sync) to update form inputs
    // before we build FormData in product-form submit.
    queueMicrotask(() => {
      this.#submitForm();
    });
  };

  /**
   * Handles mousedown for instant visual feedback
   * Mousedown fires BEFORE click, eliminating micro-drag delays
   * @param {Event} event
   */
  #handleVariantMouseDown = (event) => {
    const target = event.target;
    
    // Find the input element
    /** @type {HTMLInputElement | null} */
    let input = null;
    
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      input = target;
    } else if (target instanceof HTMLElement) {
      const label = target.closest('label');
      if (label) {
        input = label.querySelector('input[type="radio"]');
      }
    }
    
    if (!input || !input.dataset.variantId) return;

    // Prevent parent components (e.g. slideshow drag/click handlers) from
    // treating variant interaction as slide interaction.
    event.stopPropagation();

    this.#pointerDownVariantId = input.dataset.variantId;
    this.#pointerDownWasChecked = input.checked;

    const isAvailable = input.dataset.optionAvailable !== 'false';
    if (!isAvailable) return;

    // Prevent duplicate submissions
    if (this.hasAttribute('data-loading')) {
      return;
    }

    // Show loading IMMEDIATELY on mousedown
    this.#setLoading(true);
  };

  /**
   * Handles click on an already-checked variant option to add to cart
   * @param {Event} event
   */
  #handleVariantClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const label = target.closest('.variant-option__button-label-quick-add');
    if (!label) return;

    // Keep click local to quick add so parent slideshow/product-card click
    // handlers are not triggered.
    event.stopPropagation();

    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement) || !input.dataset.variantId) return;

    const isAvailable = input.dataset.optionAvailable !== 'false';
    if (!isAvailable) return;

    // Only trigger for true re-clicks of an already-selected option.
    // New selections are handled by the change handler after state sync.
    const isPointerReclick =
      this.#pointerDownVariantId === input.dataset.variantId && this.#pointerDownWasChecked;
    const isKeyboardReclick =
      this.#pointerDownVariantId === null &&
      input.checked &&
      input.dataset.variantId === this.#lastSelectedVariantId;

    this.#pointerDownVariantId = null;
    this.#pointerDownWasChecked = false;

    if (!isPointerReclick && !isKeyboardReclick) return;

    this.#syncFormVariantId(input.dataset.variantId);

    // Check if loading already set by mousedown handler
    const alreadyLoading = this.hasAttribute('data-loading');
    
    if (!alreadyLoading) {
      // Fallback for keyboard navigation (no mousedown event)
      this.#setLoading(true);
    }

    this.#submitForm();
  };

  /**
   * Handles cart update event and opens the cart drawer
   * @param {CartUpdateEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    this.#setLoading(false);
    if (event.detail?.data?.didError) return;
    this.#openCartDrawer();
  };

  /**
   * Handles cart error event and clears loading state
   * @param {Event} event - The cart error event
   */
  #handleCartError = () => {
    this.#setLoading(false);
  };

  /**
   * Triggers the product form submission
   */
  #submitForm() {
    // Prevent double-submit: when clicking a new variant, the label's default action
    // dispatches a synthetic click on the radio. Both the change handler and this
    // synthetic click's handler call #submitForm(), adding the item twice.
    if (this.#isSubmitting) return;
    this.#isSubmitting = true;
    // Reset after current synchronous event processing completes
    setTimeout(() => { this.#isSubmitting = false; }, 0);

    const productFormComponent = /** @type {HTMLElement & { handleSubmit: (event: Event) => void } | null} */ (
      this.querySelector('product-form-component')
    );

    if (!productFormComponent) {
      console.error('QuickAddWithVariantPicker: product-form-component not found');
      this.#isSubmitting = false;
      this.#setLoading(false); // Clear loading state if form not found
      return;
    }

    try {
      productFormComponent.handleSubmit(new Event('submit'));
    } catch (error) {
      console.error('QuickAddWithVariantPicker: Error submitting form', error);
      this.#isSubmitting = false;
      this.#setLoading(false); // Clear loading state on error
    }
  }

  /**
   * Syncs hidden form variant input with selected option for immediate submit paths.
   * @param {string | null | undefined} variantId
   */
  #syncFormVariantId(variantId) {
    if (!variantId) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantInput) return;

    variantInput.value = variantId;
    variantInput.disabled = false;
  }

  /**
   * Opens the cart drawer
   */
  #openCartDrawer() {
    const cartDrawer = /** @type {HTMLElement & { open: () => void } | null} */ (
      document.querySelector('cart-drawer-component')
    );
    cartDrawer?.open();
  }
}

if (!customElements.get('quick-add-with-variant-picker')) {
  customElements.define('quick-add-with-variant-picker', QuickAddWithVariantPickerComponent);
}

/**
 * Quick add product switcher component for switching between alternate color products in the modal.
 * Uses Shopify Section Rendering API to fetch and replace modal content.
 */
class QuickAddProductSwitcher extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Map<string, {html: Document, timestamp: number}>} */
  static #productCache = new Map();

  /** @type {Set<string>} */
  static #prefetchingUrls = new Set();

  /** @type {number} Cache TTL in milliseconds (5 minutes) */
  static #CACHE_TTL = 5 * 60 * 1000;

  /** @type {boolean} */
  #hasPreloaded = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleProductChange.bind(this));
    this.#syncLabelWithSelectedInput();
    // Preload alternative products (HTML + images) when switcher becomes visible
    this.#setupPreloading();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Sets up preloading when the switcher is visible in the modal
   * Since we only preload 1 image per product now, it's reasonable to load immediately
   */
  #setupPreloading() {
    // Use IntersectionObserver for mobile/modal context
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.#hasPreloaded) {
          this.#preloadAlternativeProducts();
          observer.disconnect(); // Only preload once
        }
      });
    }, { threshold: 0.1 });

    observer.observe(this);
  }

  /**
   * Preloads all alternative products (HTML + featured images)
   */
  async #preloadAlternativeProducts() {
    if (this.#hasPreloaded) return;
    this.#hasPreloaded = true;

    const inputs = this.querySelectorAll('input[type="radio"]');
    const productUrls = Array.from(inputs)
      .map(input => ({
        url: input.dataset.productUrl,
        id: input.dataset.productId
      }))
      .filter(({ url, id }) => url && id && id !== this.dataset.currentProductId);


    // Preload all alternative products in parallel (HTML + featured images)
    const preloadPromises = productUrls.map(({ url, id }) => 
      this.#prefetchProduct(url, id)
    );

    await Promise.allSettled(preloadPromises);
  }

  /**
   * Prefetches a product and stores it in cache
   * Also preloads product featured image for instant display
   * @param {string} productUrl - The product URL
   * @param {string} productId - The product ID
   */
  async #prefetchProduct(productUrl, productId) {
    // Skip if already in cache or currently prefetching
    if (QuickAddProductSwitcher.#productCache.has(productUrl) || 
        QuickAddProductSwitcher.#prefetchingUrls.has(productUrl)) {
      return;
    }

    QuickAddProductSwitcher.#prefetchingUrls.add(productUrl);

    try {
      const response = await fetch(productUrl, { 
        priority: 'low' // Low priority so it doesn't interfere with critical requests
      });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store in cache with timestamp
      QuickAddProductSwitcher.#productCache.set(productUrl, {
        html,
        timestamp: Date.now()
      });

      // Preload featured image (only 1 image per product on mobile)
      this.#preloadProductImages(html, productId);

      console.log(`QuickAddProductSwitcher: Cached product ${productId}`);
    } catch (error) {
      console.warn('QuickAddProductSwitcher: Prefetch failed for', productUrl, error);
    } finally {
      QuickAddProductSwitcher.#prefetchingUrls.delete(productUrl);
    }
  }

  /**
   * Preloads product images to browser cache
   * Only preloads the featured image since quick-add shows single image (no carousel)
   * @param {Document} html - The parsed HTML document
   * @param {string} productId - The product ID for logging
   */
  #preloadProductImages(html, productId) {
    const images = new Set();

    // Get images from product grid content
    const productGrid = html.querySelector('[data-product-grid-content]');
    if (productGrid) {
      // Only get the first/featured image (quick-add shows single image, no carousel)
      const featuredImage = productGrid.querySelector('img[src], img[data-src]');
      
      if (featuredImage) {
        // Regular src
        if (featuredImage.src && !featuredImage.src.includes('data:image')) {
          images.add(featuredImage.src);
        }
        // Lazy-loaded data-src
        if (featuredImage.dataset?.src) {
          images.add(featuredImage.dataset.src);
        }
        // Srcset for responsive images (mobile needs different sizes)
        if (featuredImage.srcset) {
          const srcsetUrls = featuredImage.srcset.split(',').map(s => s.trim().split(' ')[0]);
          srcsetUrls.forEach(url => images.add(url));
        }
      }

      // Swatch images (small, useful to preload)
      const swatchImages = productGrid.querySelectorAll('.swatch__image, [class*="swatch"] img');
      swatchImages.forEach(img => {
        if (img.src && !img.src.includes('data:image')) {
          images.add(img.src);
        }
        if (img.dataset?.src) {
          images.add(img.dataset.src);
        }
      });
    }

    // Preload all discovered images
    let loadedCount = 0;
    const totalImages = images.size;
    
    images.forEach(src => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          console.log(`QuickAddProductSwitcher: Preloaded ${loadedCount} images for product ${productId}`);
        }
      };
      img.onerror = () => {
        loadedCount++;
        console.warn(`QuickAddProductSwitcher: Failed to preload image ${src}`);
      };
      img.src = src;
    });

    if (totalImages === 0) {
      console.warn(`QuickAddProductSwitcher: No images found to preload for product ${productId}`);
    } else {
      console.log(`QuickAddProductSwitcher: Started preloading ${totalImages} images for product ${productId}`);
    }
  }

  /**
   * Gets cached product data if available and not expired
   * @param {string} productUrl - The product URL
   * @returns {Document | null}
   */
  #getCachedProduct(productUrl) {
    const cached = QuickAddProductSwitcher.#productCache.get(productUrl);
    if (!cached) return null;

    // Check if cache is expired
    const age = Date.now() - cached.timestamp;
    if (age > QuickAddProductSwitcher.#CACHE_TTL) {
      QuickAddProductSwitcher.#productCache.delete(productUrl);
      return null;
    }

    return cached.html;
  }

  /**
   * Handles the alternate product change event.
   * @param {Event} event - The change event from the radio input.
   */
  #handleProductChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    const productId = target.dataset.productId;
    const productUrl = target.dataset.productUrl;
    const productTitle = target.dataset.productTitle;

    if (!productId || !productUrl) return;

    this.#setSelectedLabel(target.dataset.productColorName || target.dataset.productTitle || '');

    const currentProductId = this.dataset.currentProductId;
    if (productId === currentProductId) return;

    this.#fetchAndUpdateModal(productUrl, productId, productTitle || '');
  }

  /**
   * Fetches the new product content and updates the modal.
   * Uses cache if available for instant updates.
   * @param {string} productUrl - The product URL.
   * @param {string} productId - The product ID.
   * @param {string} productTitle - The product title.
   */
  async #fetchAndUpdateModal(productUrl, productId, productTitle) {
    // Check cache first
    const cachedHtml = this.#getCachedProduct(productUrl);
    if (cachedHtml) {
      console.log('QuickAddProductSwitcher: Using cached product', productId);
      this.#updateModalContent(cachedHtml, productId, productUrl, productTitle);
      return;
    }

    // Not in cache - fetch from network
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.classList.add('quick-add-product-switcher--loading');

    try {
      const response = await fetch(productUrl, { signal: this.#abortController.signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store in cache
      QuickAddProductSwitcher.#productCache.set(productUrl, {
        html,
        timestamp: Date.now()
      });

      // Preload images for this product
      this.#preloadProductImages(html, productId);

      this.#updateModalContent(html, productId, productUrl, productTitle);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('QuickAddProductSwitcher: Fetch aborted');
      } else {
        console.error('QuickAddProductSwitcher: Error fetching product', error);
      }
    } finally {
      this.classList.remove('quick-add-product-switcher--loading');
    }
  }

  /**
   * Updates the quick-add modal content with the fetched product data.
   * @param {Document} html - The parsed HTML document.
   * @param {string} productId - The new product ID.
   * @param {string} productUrl - The product URL.
   * @param {string} productTitle - The product title.
   */
  #updateModalContent(html, productId, productUrl, productTitle) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const newProductGrid = html.querySelector('[data-product-grid-content]');
    if (!newProductGrid) {
      console.error('QuickAddProductSwitcher: product-grid-content not found in response');
      return;
    }

    // Update product header (title and price)
    this.#updateProductHeader(modalContent, newProductGrid, productUrl, productTitle);

    // Update media/gallery (single image on mobile)
    this.#updateMedia(modalContent, newProductGrid);

    // Update variant picker
    this.#updateVariantPicker(modalContent, newProductGrid);

    // Update product form
    this.#updateProductForm(modalContent, newProductGrid, productId);

    // Sync color availability markers (slash) from newly loaded product data
    this.#syncSwitcherAvailability(newProductGrid);

    // Update alternate product switcher data and selection
    this.#updateSwitcherState(productId, productTitle);
  }

  /**
   * Syncs quick-add switcher unavailable state from alternate-product-switcher markup.
   * Applies slash when a color product is fully sold out (all variants OOS).
   * @param {Element} newProductGrid
   */
  #syncSwitcherAvailability(newProductGrid) {
    const availabilityByProductId = this.#getAvailabilityByProductId(newProductGrid);
    if (!availabilityByProductId.size) return;

    const items = this.querySelectorAll('.quick-add-product-switcher__item');
    items.forEach((item) => {
      const input = item.querySelector('input[type="radio"][data-product-id]');
      if (!(input instanceof HTMLInputElement)) return;

      const productId = input.dataset.productId;
      if (!productId || !availabilityByProductId.has(productId)) return;

      const isUnavailable = availabilityByProductId.get(productId) === false;
      item.classList.toggle('quick-add-product-switcher__item--unavailable', isUnavailable);

      if (isUnavailable) {
        input.setAttribute('data-product-available', 'false');
      } else {
        input.removeAttribute('data-product-available');
      }

      const swatch = item.querySelector('.quick-add-product-switcher__swatch');
      if (!(swatch instanceof HTMLElement)) return;

      const existingSlash = swatch.querySelector('.quick-add-product-switcher__swatch-slash');
      if (isUnavailable && !existingSlash) {
        const slash = document.createElement('span');
        slash.className = 'quick-add-product-switcher__swatch-slash';
        slash.setAttribute('aria-hidden', 'true');
        swatch.appendChild(slash);
        return;
      }

      if (!isUnavailable && existingSlash) {
        existingSlash.remove();
      }
    });
  }

  /**
   * Builds product availability map from server-rendered alternate switcher markup.
   * @param {Element} newProductGrid
   * @returns {Map<string, boolean>}
   */
  #getAvailabilityByProductId(newProductGrid) {
    const availabilityMap = new Map();
    const alternateSwitcher = newProductGrid.querySelector('alternate-product-switcher');
    if (!alternateSwitcher) return availabilityMap;

    const items = alternateSwitcher.querySelectorAll('.alternate-product-switcher__item');
    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const input = item.querySelector('input[type="radio"][data-product-id]');
      if (!(input instanceof HTMLInputElement)) return;

      const productId = input.dataset.productId;
      if (!productId) return;

      const rawAvailability = item.dataset.available ?? item.dataset.altAvailable;
      const parsedAvailability = this.#parseAvailability(rawAvailability);

      if (parsedAvailability !== null) {
        availabilityMap.set(productId, parsedAvailability);
        return;
      }

      if (item.classList.contains('alternate-product-switcher__item--sold-out')) {
        availabilityMap.set(productId, false);
      }
    });

    return availabilityMap;
  }

  /**
   * Parses availability value coming from dataset attributes.
   * @param {string | null | undefined} value
   * @returns {boolean | null}
   */
  #parseAvailability(value) {
    if (value === 'true' || value === '1') return true;
    if (value === 'false' || value === '0') return false;
    return null;
  }

  #syncLabelWithSelectedInput() {
    const checkedInput = this.querySelector('input[type="radio"]:checked');
    if (checkedInput instanceof HTMLInputElement) {
      this.#setSelectedLabel(checkedInput.dataset.productColorName || checkedInput.dataset.productTitle || '');
    }
  }

  /**
   * @param {string} title
   */
  #setSelectedLabel(title) {
    const label = this.querySelector('.quick-add-product-switcher__label');
    if (!label) return;

    let selectedValue = label.querySelector('.variant-option__selected-value');
    if (!selectedValue) {
      selectedValue = document.createElement('span');
      selectedValue.className = 'variant-option__selected-value';
      label.appendChild(selectedValue);
    }

    selectedValue.textContent = title;
  }

  /**
   * Updates the product header (title and price).
   * @param {Element} modalContent - The modal content element.
   * @param {Element} newProductGrid - The new product grid element.
   * @param {string} productUrl - The product URL.
   * @param {string} productTitle - The product title.
   */
  #updateProductHeader(modalContent, newProductGrid, productUrl, productTitle) {
    const productHeader = modalContent.querySelector('.product-header');
    if (!productHeader) return;

    // Update title
    const titleLink = productHeader.querySelector('a');
    if (titleLink) {
      titleLink.textContent = productTitle;
      titleLink.href = productUrl;
    }

    // Update price
    const currentPrice = productHeader.querySelector('product-price');
    const newPrice = newProductGrid.querySelector('product-price');
    if (currentPrice && newPrice) {
      morph(currentPrice, newPrice);
    }
  }

  /**
   * Updates the media/gallery.
   * @param {Element} modalContent - The modal content element.
   * @param {Element} newProductGrid - The new product grid element.
   */
  #updateMedia(modalContent, newProductGrid) {
    const currentMedia = modalContent.querySelector('.product-information__media');
    const newMedia = newProductGrid.querySelector('.product-information__media');
    if (currentMedia && newMedia) {
      currentMedia.replaceWith(newMedia.cloneNode(true));
    }
  }

  /**
   * Updates the variant picker.
   * @param {Element} modalContent - The modal content element.
   * @param {Element} newProductGrid - The new product grid element.
   */
  #updateVariantPicker(modalContent, newProductGrid) {
    const currentVariantPicker = modalContent.querySelector('variant-picker');
    const newVariantPicker = newProductGrid.querySelector('variant-picker');
    if (currentVariantPicker && newVariantPicker) {
      currentVariantPicker.replaceWith(newVariantPicker.cloneNode(true));
    }
  }

  /**
   * Updates the product form component.
   * @param {Element} modalContent - The modal content element.
   * @param {Element} newProductGrid - The new product grid element.
   * @param {string} productId - The new product ID.
   */
  #updateProductForm(modalContent, newProductGrid, productId) {
    const currentForm = modalContent.querySelector('product-form-component');
    const newForm = newProductGrid.querySelector('product-form-component');

    if (currentForm && newForm) {
      const nextForm = /** @type {Element} */ (newForm.cloneNode(true));
      this.#normalizeBackInStockTrigger(nextForm);
      currentForm.replaceWith(nextForm);
    } else if (currentForm) {
      // Update existing form if new one not found
      currentForm.setAttribute('data-product-id', productId);

      const variantInput = currentForm.querySelector('input[name="id"]');
      const newVariantInput = newProductGrid.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement && newVariantInput instanceof HTMLInputElement) {
        variantInput.value = newVariantInput.value;
        variantInput.disabled = newVariantInput.disabled;
      }

      this.#normalizeBackInStockTrigger(currentForm);
    }
  }

  /**
   * Applies BIS trigger classes and removes duplicated ID in quick-add forms.
   * @param {Element} formRoot
   */
  #normalizeBackInStockTrigger(formRoot) {
    const backInStockTrigger = formRoot.querySelector('a.back-in-stock-trigger');
    if (backInStockTrigger) {
      backInStockTrigger.removeAttribute('id');
      backInStockTrigger.classList.add('BIS_trigger', 'bis-collection-button');
    }
  }

  /**
   * Updates the switcher state (current product ID and visual selection).
   * @param {string} productId - The new product ID.
   */
  /**
   * @param {string} productId
   * @param {string} productTitle
   */
  #updateSwitcherState(productId, productTitle) {
    this.dataset.currentProductId = productId;

    const items = this.querySelectorAll('.quick-add-product-switcher__item');
    let selectedColorName = '';

    items.forEach((item) => {
      const input = item.querySelector('input');
      if (input?.dataset.productId === productId) {
        item.classList.add('quick-add-product-switcher__item--selected');
        if (!selectedColorName) {
          selectedColorName = input.dataset.productColorName || input.dataset.productTitle || '';
        }
      } else {
        item.classList.remove('quick-add-product-switcher__item--selected');
      }
    });

    this.#setSelectedLabel(selectedColorName || productTitle || '');
  }
}

if (!customElements.get('quick-add-product-switcher')) {
  customElements.define('quick-add-product-switcher', QuickAddProductSwitcher);
}